import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, List, Search, Filter, RefreshCw, Clock, MapPin, ExternalLink, Repeat, CalendarDays, X, Heart, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import databaseService from './services/database';
import { DATA_REFRESH_INTERVAL, FALLBACK_TO_MOCK_DATA } from './config/environment';

// Force production behavior on Pages domains
const IS_PRODUCTION_DOMAIN = typeof window !== 'undefined' && 
  (window.location.hostname.includes('pages.dev') || 
   window.location.hostname.includes('seattle-events-aggregator') ||
   window.location.hostname.includes('.dev') ||
   !window.location.hostname.includes('localhost'));
const USE_FALLBACK = FALLBACK_TO_MOCK_DATA && !IS_PRODUCTION_DOMAIN;

// Additional production domain logging
if (typeof window !== 'undefined') {
  console.log('🌐 Domain Analysis:', {
    hostname: window.location.hostname,
    isProductionDomain: IS_PRODUCTION_DOMAIN,
    fallbackToMockData: FALLBACK_TO_MOCK_DATA,
    useFallback: USE_FALLBACK,
    includes_pages_dev: window.location.hostname.includes('pages.dev'),
    includes_seattle_events: window.location.hostname.includes('seattle-events-aggregator'),
    includes_dev: window.location.hostname.includes('.dev'),
    not_localhost: !window.location.hostname.includes('localhost')
  });
}

// Mock event sources data for fallback and source display
const mockEventSources = [
  {
    name: 'SeattleMet',
    url: 'seattlemet.com',
    events: [
      {
        id: 1,
        title: 'Jazz Night at Dimitriou\'s Jazz Alley',
        date: '2025-07-18',
        time: '8:00 PM',
        location: 'Dimitriou\'s Jazz Alley, Seattle',
        description: 'An evening of smooth jazz featuring local and touring musicians in Seattle\'s premier jazz venue.',
        category: 'Music',
        source: 'SeattleMet',
        url: 'https://www.seattlemet.com/arts-and-culture/jazz-night-dimitrious-jazz-alley',
        eventType: 'single'
      },
      {
        id: 2,
        title: 'Pike Place Market Food Tour',
        date: '2025-07-19',
        time: '11:00 AM',
        location: 'Pike Place Market, Seattle',
        description: 'Guided culinary tour through Pike Place Market featuring tastings from local vendors.',
        category: 'Food',
        source: 'SeattleMet',
        url: 'https://www.seattlemet.com/eat-and-drink/pike-place-market-food-tour',
        eventType: 'recurring',
        recurrence: {
          frequency: 'daily',
          daysOfWeek: ['Tuesday', 'Thursday', 'Saturday'],
          endDate: '2025-08-31'
        }
      },
      {
        id: 15,
        title: 'Seattle International Film Festival',
        startDate: '2025-07-24',
        endDate: '2025-08-02',
        time: 'Various times',
        location: 'Multiple venues across Seattle',
        description: 'Annual celebration of international cinema with screenings, premieres, and filmmaker talks across the city.',
        category: 'Film',
        source: 'SeattleMet',
        url: 'https://www.seattlemet.com/arts-and-culture/siff-2025',
        eventType: 'multiDay',
        dailySchedule: [
          { date: '2025-07-24', times: ['7:00 PM', '9:30 PM'], focus: 'Opening Night Gala' },
          { date: '2025-07-25', times: ['2:00 PM', '5:00 PM', '8:00 PM'], focus: 'International Shorts' },
          { date: '2025-07-26', times: ['1:00 PM', '4:00 PM', '7:00 PM'], focus: 'Documentary Day' }
        ]
      }
    ]
  },
  {
    name: 'EverOut',
    url: 'everout.com',
    events: [
      {
        id: 3,
        title: 'Capitol Hill Art Walk',
        date: '2025-07-17',
        time: '6:00 PM',
        location: 'Capitol Hill, Seattle',
        description: 'Monthly art walk featuring galleries, studios, and pop-up exhibitions throughout Capitol Hill.',
        category: 'Arts',
        source: 'EverOut',
        url: 'https://everout.com/seattle/events/capitol-hill-art-walk-july/e150234/',
        eventType: 'recurring',
        recurrence: {
          frequency: 'monthly',
          dayOfMonth: 'second Thursday',
          endDate: '2025-12-31'
        }
      },
      {
        id: 4,
        title: 'Indie Film Screening: Local Stories',
        date: '2025-07-20',
        time: '7:30 PM',
        location: 'SIFF Cinema Uptown, Seattle',
        description: 'Special screening of locally-produced independent films with director Q&A.',
        category: 'Film',
        source: 'EverOut',
        url: 'https://everout.com/seattle/events/indie-film-screening-local-stories/e150456/',
        eventType: 'single'
      }
    ]
  },
  {
    name: 'VisitSeattle',
    url: 'visitseattle.org',
    events: [
      {
        id: 5,
        title: 'Waterfront Summer Concert Series',
        date: '2025-07-21',
        time: '7:00 PM',
        location: 'Waterfront Park, Seattle',
        description: 'Free outdoor concert featuring Pacific Northwest bands with views of Elliott Bay.',
        category: 'Music',
        source: 'VisitSeattle',
        url: 'https://visitseattle.org/events/waterfront-summer-concert-series/',
        eventType: 'recurring',
        recurrence: {
          frequency: 'weekly',
          daysOfWeek: ['Monday'],
          endDate: '2025-09-01'
        }
      },
      {
        id: 6,
        title: 'Space Needle Night Market',
        date: '2025-07-22',
        time: '5:00 PM',
        location: 'Seattle Center, Seattle',
        description: 'Evening market with local artisans, food trucks, and live entertainment at Seattle Center.',
        category: 'Market',
        source: 'VisitSeattle',
        url: 'https://visitseattle.org/events/space-needle-night-market/',
        eventType: 'single'
      },
      {
        id: 16,
        title: 'Chihuly Garden and Glass: Summer Exhibition',
        startDate: '2025-06-01',
        endDate: '2025-09-30',
        time: '9:00 AM - 7:00 PM',
        location: 'Chihuly Garden and Glass, Seattle Center',
        description: 'Extended summer exhibition featuring new installations and extended evening hours with special lighting.',
        category: 'Arts',
        source: 'VisitSeattle',
        url: 'https://visitseattle.org/events/chihuly-summer-exhibition/',
        eventType: 'ongoing'
      },
      // Duplicate event for testing (same as EverOut's Capitol Hill Art Walk)
      {
        id: 17,
        title: 'Capitol Hill Monthly Art Walk',
        date: '2025-07-17',
        time: '6:00 PM',
        location: 'Capitol Hill, Seattle',
        description: 'Monthly gallery walk featuring local artists and studios throughout the Capitol Hill neighborhood.',
        category: 'Arts',
        source: 'VisitSeattle',
        url: 'https://visitseattle.org/events/capitol-hill-art-walk/',
        eventType: 'single'
      }
    ]
  },
  {
    name: 'SeattleMag',
    url: 'seattlemag.com',
    events: [
      {
        id: 7,
        title: 'Fremont Sunday Market',
        date: '2025-07-20',
        time: '10:00 AM',
        location: 'Fremont, Seattle',
        description: 'Weekly farmers market and artisan fair in the heart of quirky Fremont neighborhood.',
        category: 'Market',
        source: 'SeattleMag',
        url: 'https://seattlemag.com/calendar/fremont-sunday-market-july',
        eventType: 'recurring',
        recurrence: {
          frequency: 'weekly',
          daysOfWeek: ['Sunday'],
          endDate: '2025-10-31'
        }
      },
      {
        id: 8,
        title: 'Ballard Beer & Food Festival',
        startDate: '2025-07-19',
        endDate: '2025-07-20',
        time: '2:00 PM - 10:00 PM',
        location: 'Ballard Commons Park, Seattle',
        description: 'Two-day celebration of local breweries and restaurants with tastings, live music, and food competitions.',
        category: 'Food',
        source: 'SeattleMag',
        url: 'https://seattlemag.com/calendar/ballard-beer-food-festival',
        eventType: 'multiDay',
        dailySchedule: [
          { date: '2025-07-19', times: ['2:00 PM - 10:00 PM'], focus: 'Brewery Showcase & Live Music' },
          { date: '2025-07-20', times: ['12:00 PM - 8:00 PM'], focus: 'Food Competition & Family Day' }
        ]
      },
      // Duplicate event for testing (same as VisitSeattle's Waterfront Summer Concert)
      {
        id: 18,
        title: 'Seattle Waterfront Concert Series',
        date: '2025-07-21',
        time: '7:30 PM',
        location: 'Waterfront Park, Seattle',
        description: 'Free outdoor summer concert series featuring Pacific Northwest musicians with stunning Elliott Bay views.',
        category: 'Music',
        source: 'SeattleMag',
        url: 'https://seattlemag.com/calendar/waterfront-concert-series',
        eventType: 'single'
      }
    ]
  },
  {
    name: 'Fever',
    url: 'feverup.com',
    events: [
      {
        id: 9,
        title: 'Immersive Van Gogh Experience',
        date: '2025-07-23',
        time: '6:00 PM',
        location: 'Lighthouse ArtSpace, Seattle',
        description: 'Digital art exhibition featuring Van Gogh\'s masterpieces in an immersive environment.',
        category: 'Arts',
        source: 'Fever',
        url: 'https://feverup.com/en/seattle/plans/immersive-van-gogh-seattle',
        eventType: 'single'
      },
      {
        id: 10,
        title: 'Rooftop Yoga with City Views',
        date: '2025-07-18',
        time: '6:30 AM',
        location: 'Downtown Seattle Rooftop',
        description: 'Morning yoga session on a downtown rooftop with panoramic views of the city and mountains.',
        category: 'Wellness',
        source: 'Fever',
        url: 'https://feverup.com/en/seattle/plans/rooftop-yoga-city-views',
        eventType: 'recurring',
        recurrence: {
          frequency: 'weekly',
          daysOfWeek: ['Tuesday', 'Friday'],
          endDate: '2025-12-31'
        }
      },
      // Duplicate event for testing (same as SeattleMet's Pike Place Market Food Tour)
      {
        id: 11,
        title: 'Pike Place Market Culinary Tour',
        date: '2025-07-19',
        time: '11:30 AM',
        location: 'Pike Place Market, Seattle',
        description: 'Guided food tasting tour through historic Pike Place Market with local vendor tastings.',
        category: 'Food',
        source: 'Fever',
        url: 'https://feverup.com/en/seattle/plans/pike-place-market-food-tour',
        eventType: 'single'
      }
    ]
  },
  {
    name: 'The Stranger',
    url: 'thestranger.com',
    events: [
      {
        id: 19,
        title: 'Indie Rock Night at The Crocodile',
        date: '2025-07-18',
        time: '9:00 PM',
        location: 'The Crocodile Cafe, Seattle',
        description: 'Local indie bands take the stage at Seattle\'s legendary music venue with three acts and late-night vibes.',
        category: 'Music',
        source: 'The Stranger',
        url: 'https://www.thestranger.com/events/indie-rock-night-crocodile',
        eventType: 'single'
      },
      {
        id: 20,
        title: 'Capitol Hill Comedy Open Mic',
        date: '2025-07-17',
        time: '8:00 PM',
        location: 'Laughs Comedy Spot, Capitol Hill',
        description: 'Weekly open mic night featuring emerging comedians and seasoned performers in an intimate setting.',
        category: 'Comedy',
        source: 'The Stranger',
        url: 'https://www.thestranger.com/events/capitol-hill-comedy-open-mic',
        eventType: 'recurring',
        recurrence: {
          frequency: 'weekly',
          daysOfWeek: ['Wednesday'],
          endDate: '2025-12-31'
        }
      },
      {
        id: 21,
        title: 'Underground Art Show: Emerging Artists',
        date: '2025-07-20',
        time: '7:00 PM',
        location: 'Vermillion Art Gallery, Pioneer Square',
        description: 'Showcase of local emerging artists working in experimental media, performance, and street art.',
        category: 'Arts',
        source: 'The Stranger',
        url: 'https://www.thestranger.com/events/underground-art-show-emerging-artists',
        eventType: 'single'
      },
      {
        id: 22,
        title: 'Friday Night Drag Show',
        date: '2025-07-18',
        time: '10:30 PM',
        location: 'Unicorn Bar, Capitol Hill',
        description: 'Weekly drag performance featuring local queens and kings with music, comedy, and fabulous entertainment.',
        category: 'Nightlife',
        source: 'The Stranger',
        url: 'https://www.thestranger.com/events/friday-night-drag-show',
        eventType: 'recurring',
        recurrence: {
          frequency: 'weekly',
          daysOfWeek: ['Friday'],
          endDate: '2025-12-31'
        }
      },
      {
        id: 23,
        title: 'Alternative Film Festival',
        startDate: '2025-07-25',
        endDate: '2025-07-27',
        time: 'Various times',
        location: 'Northwest Film Forum, Capitol Hill',
        description: 'Three-day festival showcasing independent, documentary, and experimental films from Pacific Northwest filmmakers.',
        category: 'Film',
        source: 'The Stranger',
        url: 'https://www.thestranger.com/events/alternative-film-festival',
        eventType: 'multiDay',
        dailySchedule: [
          { date: '2025-07-25', times: ['7:00 PM', '9:30 PM'], focus: 'Documentary Premieres' },
          { date: '2025-07-26', times: ['2:00 PM', '5:00 PM', '8:00 PM'], focus: 'Experimental Shorts' },
          { date: '2025-07-27', times: ['1:00 PM', '4:00 PM', '7:00 PM'], focus: 'Director Q&As' }
        ]
      }
    ]
  },
  {
    name: 'Seattle Theatre Group',
    url: 'seattletheatregroup.org',
    events: [
      {
        id: 24,
        title: 'Hamilton',
        date: '2025-07-22',
        time: '7:30 PM',
        location: 'Paramount Theatre, Seattle',
        description: 'The groundbreaking musical about Alexander Hamilton returns to Seattle with the original Broadway staging.',
        category: 'Theatre',
        source: 'Seattle Theatre Group',
        url: 'https://www.seattletheatregroup.org/tickets/hamilton',
        eventType: 'single'
      },
      {
        id: 25,
        title: 'Seattle Symphony at Benaroya Hall',
        date: '2025-07-19',
        time: '8:00 PM',
        location: 'Benaroya Hall, Seattle',
        description: 'Seattle Symphony performs Beethoven\'s 9th Symphony with full orchestra and chorus.',
        category: 'Music',
        source: 'Seattle Theatre Group',
        url: 'https://www.seattletheatregroup.org/tickets/seattle-symphony-beethoven-9',
        eventType: 'single'
      },
      {
        id: 26,
        title: 'Moore Theatre Concert Series',
        date: '2025-07-21',
        time: '8:00 PM',
        location: 'Moore Theatre, Seattle',
        description: 'Weekly concert series featuring touring indie and alternative artists in an intimate historic venue.',
        category: 'Music',
        source: 'Seattle Theatre Group',
        url: 'https://www.seattletheatregroup.org/tickets/moore-concert-series',
        eventType: 'recurring',
        recurrence: {
          frequency: 'weekly',
          daysOfWeek: ['Monday'],
          endDate: '2025-12-31'
        }
      },
      {
        id: 27,
        title: 'The Lion King',
        startDate: '2025-07-24',
        endDate: '2025-08-10',
        time: '7:30 PM',
        location: 'Paramount Theatre, Seattle',
        description: 'Disney\'s award-winning musical brings the Pride Lands to life with stunning costumes, music, and puppetry.',
        category: 'Theatre',
        source: 'Seattle Theatre Group',
        url: 'https://www.seattletheatregroup.org/tickets/the-lion-king',
        eventType: 'ongoing'
      },
      {
        id: 28,
        title: 'Broadway Bound Summer Workshop',
        startDate: '2025-07-28',
        endDate: '2025-07-30',
        time: '10:00 AM - 4:00 PM',
        location: 'Neptune Theatre, Seattle',
        description: 'Three-day intensive workshop for aspiring musical theatre performers with industry professionals.',
        category: 'Theatre',
        source: 'Seattle Theatre Group',
        url: 'https://www.seattletheatregroup.org/education/broadway-bound-workshop',
        eventType: 'multiDay',
        dailySchedule: [
          { date: '2025-07-28', times: ['10:00 AM - 4:00 PM'], focus: 'Voice and Movement' },
          { date: '2025-07-29', times: ['10:00 AM - 4:00 PM'], focus: 'Acting and Scene Work' },
          { date: '2025-07-30', times: ['10:00 AM - 4:00 PM'], focus: 'Final Showcase' }
        ]
      }
    ]
  },
  {
    name: 'Benaroya Hall',
    url: 'seattlesymphony.org',
    events: [
      {
        id: 29,
        title: 'Seattle Symphony: Mahler Symphony No. 5',
        date: '2025-07-17',
        time: '8:00 PM',
        location: 'Benaroya Hall, Seattle',
        description: 'Thomas Dausgaard conducts the Seattle Symphony in Mahler\'s emotionally powerful Fifth Symphony.',
        category: 'Music',
        source: 'Benaroya Hall',
        url: 'https://www.seattlesymphony.org/concerts/mahler-symphony-5',
        eventType: 'single'
      },
      {
        id: 30,
        title: 'Chamber Music Series',
        date: '2025-07-20',
        time: '7:30 PM',
        location: 'Benaroya Hall Recital Hall, Seattle',
        description: 'Intimate chamber music performances featuring Seattle Symphony musicians in smaller ensemble works.',
        category: 'Music',
        source: 'Benaroya Hall',
        url: 'https://www.seattlesymphony.org/concerts/chamber-music-series',
        eventType: 'recurring',
        recurrence: {
          frequency: 'weekly',
          daysOfWeek: ['Saturday'],
          endDate: '2025-12-31'
        }
      },
      {
        id: 31,
        title: 'Pops Concert: Movie Magic',
        date: '2025-07-23',
        time: '8:00 PM',
        location: 'Benaroya Hall, Seattle',
        description: 'Seattle Symphony performs beloved film scores from Star Wars, Harry Potter, and other cinematic classics.',
        category: 'Music',
        source: 'Benaroya Hall',
        url: 'https://www.seattlesymphony.org/concerts/pops-movie-magic',
        eventType: 'single'
      },
      {
        id: 32,
        title: 'Piano Recital: Lang Lang',
        date: '2025-07-25',
        time: '8:00 PM',
        location: 'Benaroya Hall, Seattle',
        description: 'World-renowned pianist Lang Lang performs a solo recital featuring works by Chopin, Liszt, and contemporary composers.',
        category: 'Music',
        source: 'Benaroya Hall',
        url: 'https://www.seattlesymphony.org/concerts/lang-lang-piano-recital',
        eventType: 'single'
      },
      {
        id: 33,
        title: 'Summer Classical Festival',
        startDate: '2025-07-26',
        endDate: '2025-07-28',
        time: 'Multiple performances',
        location: 'Benaroya Hall, Seattle',
        description: 'Three-day festival celebrating classical music with guest orchestras, soloists, and masterclasses.',
        category: 'Music',
        source: 'Benaroya Hall',
        url: 'https://www.seattlesymphony.org/concerts/summer-classical-festival',
        eventType: 'multiDay',
        dailySchedule: [
          { date: '2025-07-26', times: ['2:00 PM', '8:00 PM'], focus: 'Opening Gala with Guest Orchestra' },
          { date: '2025-07-27', times: ['11:00 AM', '3:00 PM', '7:30 PM'], focus: 'Masterclasses and Recitals' },
          { date: '2025-07-28', times: ['2:00 PM', '7:00 PM'], focus: 'Grand Finale Concert' }
        ]
      }
    ]
  }
];

const EventsAggregator = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [viewMode, setViewMode] = useState('calendar');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [showRecurringInstances, setShowRecurringInstances] = useState(true);
  const [sourceSlideOut, setSourceSlideOut] = useState(null);
  const [favorites, setFavorites] = useState(new Set());
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const eventsPerPage = 10;

  // Favorites management functions
  const toggleFavorite = (eventId) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(eventId)) {
        newFavorites.delete(eventId);
      } else {
        newFavorites.add(eventId);
      }
      return newFavorites;
    });
  };

  const isFavorited = (eventId) => {
    return favorites.has(eventId);
  };

  const clearAllFavorites = () => {
    setFavorites(new Set());
  };

  // Duplicate detection utilities
  const calculateTitleSimilarity = (title1, title2) => {
    const normalize = (str) => str.toLowerCase().replace(/[^\w\s]/g, '').trim();
    const words1 = normalize(title1).split(/\s+/);
    const words2 = normalize(title2).split(/\s+/);
    
    const allWords = new Set([...words1, ...words2]);
    const commonWords = words1.filter(word => words2.includes(word));
    
    return commonWords.length / allWords.size;
  };

  const extractLocationKeywords = (location) => {
    const normalized = location.toLowerCase();
    const keywords = [];
    
    // Extract venue names and key location identifiers
    if (normalized.includes('pike place')) keywords.push('pike_place');
    if (normalized.includes('capitol hill')) keywords.push('capitol_hill');
    if (normalized.includes('fremont')) keywords.push('fremont');
    if (normalized.includes('ballard')) keywords.push('ballard');
    if (normalized.includes('seattle center')) keywords.push('seattle_center');
    if (normalized.includes('waterfront')) keywords.push('waterfront');
    if (normalized.includes('dimitriou')) keywords.push('dimitriou');
    if (normalized.includes('benaroya hall')) keywords.push('benaroya_hall');
    if (normalized.includes('paramount theatre')) keywords.push('paramount_theatre');
    if (normalized.includes('moore theatre')) keywords.push('moore_theatre');
    if (normalized.includes('neptune theatre')) keywords.push('neptune_theatre');
    if (normalized.includes('crocodile')) keywords.push('crocodile_cafe');
    if (normalized.includes('unicorn bar')) keywords.push('unicorn_bar');
    if (normalized.includes('pioneer square')) keywords.push('pioneer_square');
    if (normalized.includes('northwest film forum')) keywords.push('film_forum');
    
    return keywords;
  };

  const isTimesSimilar = (time1, time2) => {
    // Simple time similarity - within 1 hour
    const parseTime = (timeStr) => {
      const match = timeStr.match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)?/i);
      if (!match) return null;
      
      let hours = parseInt(match[1]);
      const minutes = parseInt(match[2] || '0');
      const period = match[3]?.toUpperCase();
      
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      
      return hours * 60 + minutes;
    };

    const minutes1 = parseTime(time1);
    const minutes2 = parseTime(time2);
    
    if (!minutes1 || !minutes2) return false;
    return Math.abs(minutes1 - minutes2) <= 60; // Within 1 hour
  };

  const isDuplicate = (event1, event2) => {
    // Must be same date
    if (event1.date !== event2.date) return false;
    
    // Must be different sources
    if (event1.source === event2.source) return false;
    
    // Calculate similarities
    const titleSim = calculateTitleSimilarity(event1.title, event2.title);
    const locationKeywords1 = extractLocationKeywords(event1.location);
    const locationKeywords2 = extractLocationKeywords(event2.location);
    const hasLocationMatch = locationKeywords1.some(keyword => locationKeywords2.includes(keyword));
    const timeSimilar = isTimesSimilar(event1.time, event2.time);
    
    // High confidence duplicate criteria
    return (titleSim >= 0.8 && hasLocationMatch) || 
           (titleSim >= 0.6 && hasLocationMatch && timeSimilar);
  };

  const createEventFingerprint = (event) => {
    const date = event.date || event.startDate;
    const titleStart = event.title.substring(0, 10).toLowerCase().replace(/[^\w]/g, '');
    return `${date}-${titleStart}`;
  };

  const deduplicateEvents = (events) => {
    // Group events by fingerprint for efficient comparison
    const fingerprintGroups = {};
    events.forEach(event => {
      const fingerprint = createEventFingerprint(event);
      if (!fingerprintGroups[fingerprint]) {
        fingerprintGroups[fingerprint] = [];
      }
      fingerprintGroups[fingerprint].push(event);
    });

    const processedEvents = [];
    const processedIds = new Set();

    // Process each fingerprint group
    Object.values(fingerprintGroups).forEach(group => {
      if (group.length === 1) {
        // No potential duplicates
        processedEvents.push(group[0]);
        processedIds.add(group[0].id);
        return;
      }

      // Find duplicates within the group
      const duplicateGroups = [];
      const remaining = [...group];

      while (remaining.length > 0) {
        const current = remaining.shift();
        if (processedIds.has(current.id)) continue;

        const duplicates = [current];
        
        // Find all duplicates of current event
        for (let i = remaining.length - 1; i >= 0; i--) {
          if (isDuplicate(current, remaining[i])) {
            duplicates.push(remaining.splice(i, 1)[0]);
          }
        }

        if (duplicates.length > 1) {
          // Merge duplicates
          const mergedEvent = mergeDuplicates(duplicates);
          processedEvents.push(mergedEvent);
          duplicates.forEach(event => processedIds.add(event.id));
        } else {
          // No duplicates found
          processedEvents.push(current);
          processedIds.add(current.id);
        }
      }
    });

    return processedEvents;
  };

  const mergeDuplicates = (duplicates) => {
    // Choose the best version of each field
    const primary = duplicates[0];
    
    // Use longest title as it's usually most descriptive
    const bestTitle = duplicates.reduce((best, event) => 
      event.title.length > best.title.length ? event : best
    ).title;

    // Use longest description
    const bestDescription = duplicates.reduce((best, event) => 
      event.description.length > best.description.length ? event : best
    ).description;

    // Collect all sources
    const sources = duplicates.map(event => ({
      source: event.source,
      url: event.url,
      originalTitle: event.title,
      originalTime: event.time
    }));

    return {
      ...primary,
      id: `merged-${duplicates.map(d => d.id).join('-')}`,
      title: bestTitle,
      description: bestDescription,
      isDuplicate: true,
      duplicateCount: duplicates.length,
      allSources: sources,
      mergedFrom: duplicates
    };
  };

  // Generate recurring event instances
  const generateRecurringInstances = (event, maxInstances = 5) => {
    if (event.eventType !== 'recurring') return [];
    
    const instances = [];
    const startDate = new Date(event.date);
    const endDate = event.recurrence.endDate ? new Date(event.recurrence.endDate) : new Date('2025-12-31');
    
    let currentDate = new Date(startDate);
    let instanceCount = 0;
    
    while (currentDate <= endDate && instanceCount < maxInstances) {
      if (event.recurrence.frequency === 'weekly') {
        const dayName = currentDate.toLocaleDateString('en-US', { weekday: 'long' });
        if (event.recurrence.daysOfWeek.includes(dayName)) {
          instances.push({
            ...event,
            id: `${event.id}-instance-${instanceCount}`,
            date: currentDate.toISOString().split('T')[0],
            isRecurringInstance: true,
            parentEventId: event.id,
            instanceNumber: instanceCount + 1
          });
          instanceCount++;
        }
      } else if (event.recurrence.frequency === 'daily') {
        const dayName = currentDate.toLocaleDateString('en-US', { weekday: 'long' });
        if (event.recurrence.daysOfWeek.includes(dayName)) {
          instances.push({
            ...event,
            id: `${event.id}-instance-${instanceCount}`,
            date: currentDate.toISOString().split('T')[0],
            isRecurringInstance: true,
            parentEventId: event.id,
            instanceNumber: instanceCount + 1
          });
          instanceCount++;
        }
      } else if (event.recurrence.frequency === 'monthly') {
        // Simplified monthly logic - would need more sophisticated date handling for production
        instances.push({
          ...event,
          id: `${event.id}-instance-${instanceCount}`,
          date: currentDate.toISOString().split('T')[0],
          isRecurringInstance: true,
          parentEventId: event.id,
          instanceNumber: instanceCount + 1
        });
        instanceCount++;
        currentDate.setMonth(currentDate.getMonth() + 1);
        continue;
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return instances;
  };

  // Generate multi-day event instances
  const generateMultiDayInstances = (event) => {
    if (event.eventType !== 'multiDay') return [];
    
    const instances = [];
    const startDate = new Date(event.startDate);
    const endDate = new Date(event.endDate);
    
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      const daySchedule = event.dailySchedule?.find(schedule => schedule.date === dateStr);
      
      instances.push({
        ...event,
        id: `${event.id}-day-${dateStr}`,
        date: dateStr,
        time: daySchedule?.times.join(', ') || event.time,
        description: daySchedule ? `${event.description} Today's focus: ${daySchedule.focus}` : event.description,
        isMultiDayInstance: true,
        parentEventId: event.id,
        dayFocus: daySchedule?.focus
      });
    }
    
    return instances;
  };

  // Process all events and generate instances
  const processEvents = (rawEvents) => {
    const processed = [];
    
    rawEvents.forEach(event => {
      if (event.eventType === 'single') {
        processed.push(event);
      } else if (event.eventType === 'recurring') {
        // Add the original event as a template
        processed.push({
          ...event,
          isTemplate: true,
          nextOccurrence: event.date
        });
        
        // Add instances if user wants to see them
        if (showRecurringInstances) {
          const instances = generateRecurringInstances(event);
          processed.push(...instances);
        }
      } else if (event.eventType === 'multiDay') {
        const instances = generateMultiDayInstances(event);
        processed.push(...instances);
      } else if (event.eventType === 'ongoing') {
        processed.push({
          ...event,
          date: event.startDate,
          isOngoing: true
        });
      }
    });

    // Apply duplicate detection and merging
    return deduplicateEvents(processed);
  };

  // Fetch events from database
  const scrapeEvents = async () => {
    setLoading(true);
    console.log('🔄 Starting data fetch...', {
      environment: process.env.NODE_ENV,
      hostname: typeof window !== 'undefined' ? window.location.hostname : 'server',
      fallbackEnabled: FALLBACK_TO_MOCK_DATA,
      isProductionDomain: IS_PRODUCTION_DOMAIN,
      useFallback: USE_FALLBACK
    });
    
    try {
      // Clear any previous errors and set loading state
      setApiError(null);
      setLoading(true);
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Fetch events from database
      console.log('📡 Fetching events from database service...');
      const dbEvents = await databaseService.getEvents();
      console.log('✅ Database events received:', dbEvents.length, 'events');
      
      // Process events (duplicate detection, recurring instances, etc.)
      const processedEvents = processEvents(dbEvents);
      console.log('🔄 Processed events:', processedEvents.length, 'events after processing');
      
      setEvents(processedEvents);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('❌ Failed to fetch events from database:', error);
      console.log('🔧 Fallback enabled?', USE_FALLBACK, '(original:', FALLBACK_TO_MOCK_DATA, ', production domain:', IS_PRODUCTION_DOMAIN, ')');
      
      // Check for different types of API errors
      if (error.message === 'API_BLOCKED_BY_ADBLOCKER') {
        setApiError({
          type: 'adblocker',
          message: 'Content blocked by ad blocker',
          suggestion: 'Please disable your ad blocker or add this site to your allowlist to view events.'
        });
        setEvents([]);
        setLastUpdated(new Date());
        return;
      }
      
      if (error.message === 'API_PROXY_FAILED') {
        setApiError({
          type: 'proxy_failed',
          message: 'API proxy connection failed',
          suggestion: 'The Pages Functions proxy is not working properly. Please try refreshing the page.'
        });
        setEvents([]);
        setLastUpdated(new Date());
        return;
      }
      
      if (error.message === 'API_PROXY_CONFIG_ERROR') {
        setApiError({
          type: 'proxy_config',
          message: 'API proxy configuration error',
          suggestion: 'The proxy is misconfigured and returning HTML instead of event data.'
        });
        setEvents([]);
        setLastUpdated(new Date());
        return;
      }
      
      // Check if we got HTML instead of JSON (redirect/proxy issue)
      if (error.message === 'API_RETURNED_HTML') {
        setApiError({
          type: 'redirect_issue',
          message: 'API configuration issue',
          suggestion: 'The API endpoint is returning web page content instead of event data. This is likely a configuration issue.'
        });
        setEvents([]);
        setLastUpdated(new Date());
        return;
      }
      
      // Fallback to mock data if enabled for this environment
      if (USE_FALLBACK) {
        console.log('🔄 Falling back to mock data...');
        const allRawEvents = mockEventSources.flatMap(source => source.events);
        const processedEvents = processEvents(allRawEvents);
        console.log('📊 Mock data processed:', processedEvents.length, 'events');
        setEvents(processedEvents);
        setLastUpdated(new Date());
      } else {
        // In production domains, show error state
        console.log('🚫 PRODUCTION DOMAIN DETECTED - NO FALLBACK TO MOCK DATA ALLOWED');
        console.log('🚫 Domain:', typeof window !== 'undefined' ? window.location.hostname : 'unknown');
        console.log('🚫 IS_PRODUCTION_DOMAIN:', IS_PRODUCTION_DOMAIN);
        console.log('🚫 USE_FALLBACK:', USE_FALLBACK);
        console.log('🚫 Setting events to empty array - no mock data will be shown');
        setApiError({
          type: 'api_failure',
          message: 'Unable to load events',
          suggestion: 'Please try refreshing the page or check your internet connection.'
        });
        setEvents([]);
        setLastUpdated(new Date());
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch metadata when component mounts
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [categories, sources] = await Promise.all([
          databaseService.getCategories(),
          databaseService.getSources()
        ]);
        setAvailableCategories(categories);
        setAvailableSources(sources);
      } catch (error) {
        console.error('Failed to fetch metadata:', error);
      }
    };
    
    fetchMetadata();
    scrapeEvents();
  }, [showRecurringInstances]);

  useEffect(() => {
    const interval = setInterval(() => {
      scrapeEvents();
    }, DATA_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableSources, setAvailableSources] = useState([]);
  
  const categories = availableCategories.length > 0 ? availableCategories : [...new Set(events.map(event => event.category))];
  const sources = availableSources.length > 0 ? availableSources : [...new Set(events.map(event => event.source))];

  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          event.location.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = !selectedCategory || event.category === selectedCategory;
      const matchesSource = !selectedSource || event.source === selectedSource;
      const matchesDate = !dateFilter || event.date === dateFilter;
      const matchesEventType = !eventTypeFilter || 
        (eventTypeFilter === 'single' && event.eventType === 'single') ||
        (eventTypeFilter === 'recurring' && (event.eventType === 'recurring' || event.isRecurringInstance)) ||
        (eventTypeFilter === 'multiDay' && (event.eventType === 'multiDay' || event.isMultiDayInstance)) ||
        (eventTypeFilter === 'ongoing' && event.eventType === 'ongoing');
      
      const matchesFavorites = !showFavoritesOnly || isFavorited(event.id);

      return matchesSearch && matchesCategory && matchesSource && matchesDate && matchesEventType && matchesFavorites;
    });
  }, [events, searchTerm, selectedCategory, selectedSource, dateFilter, eventTypeFilter, showFavoritesOnly, favorites]);

  // Pagination logic
  const totalPages = Math.ceil(filteredEvents.length / eventsPerPage);
  const startIndex = (currentPage - 1) * eventsPerPage;
  const endIndex = startIndex + eventsPerPage;
  
  const paginatedEvents = useMemo(() => {
    return filteredEvents.slice(startIndex, endIndex);
  }, [filteredEvents, startIndex, endIndex]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, selectedSource, dateFilter, eventTypeFilter, showFavoritesOnly, showRecurringInstances]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (start.getMonth() === end.getMonth()) {
      return `${start.toLocaleDateString('en-US', { month: 'short' })} ${start.getDate()}-${end.getDate()}`;
    }
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  const getEventTypeIcon = (event) => {
    if (event.isRecurringInstance || event.eventType === 'recurring') {
      return <Repeat className="w-3 h-3 cursor-pointer hover:text-blue-800 transition-colors" onClick={() => setSourceSlideOut(event)} title="Click to view recurrence details" />;
    }
    if (event.isMultiDayInstance || event.eventType === 'multiDay') {
      return <CalendarDays className="w-3 h-3 cursor-pointer hover:text-blue-800 transition-colors" onClick={() => setSourceSlideOut(event)} title="Click to view daily schedule" />;
    }
    if (event.isOngoing) {
      return <Clock className="w-3 h-3 cursor-pointer hover:text-blue-800 transition-colors" onClick={() => setSourceSlideOut(event)} title="Click to view ongoing details" />;
    }
    return null;
  };

  const getEventTypeLabel = (event) => {
    return '';
  };

  // Heart Icon Component
  const HeartIcon = ({ eventId, className = "" }) => {
    const favorited = isFavorited(eventId);
    
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleFavorite(eventId);
        }}
        className={`p-1 rounded-full hover:bg-gray-100 transition-all duration-200 ${className}`}
        title={favorited ? "Remove from favorites" : "Add to favorites"}
      >
        <Heart 
          className={`w-4 h-4 transition-all duration-200 ${
            favorited 
              ? 'fill-red-500 text-red-500 scale-110' 
              : 'text-gray-400 hover:text-red-400'
          }`}
        />
      </button>
    );
  };

  const EventCard = ({ event }) => {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-semibold text-gray-900 leading-tight">{event.title}</h3>
              {getEventTypeIcon(event) && (
                <span className="text-blue-600" title={getEventTypeLabel(event)}>
                  {getEventTypeIcon(event)}
                </span>
              )}
            </div>
            {getEventTypeLabel(event) && (
              <p className="text-xs text-blue-600 font-medium">{getEventTypeLabel(event)}</p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-2">
              <HeartIcon eventId={event.id} />
              {event.isDuplicate ? (
                <button
                  onClick={() => setSourceSlideOut(event)}
                  className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full whitespace-nowrap hover:bg-green-200 transition-colors cursor-pointer"
                  title="Click to view all sources"
                >
                  📊 From {event.duplicateCount} sources
                </button>
              ) : (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full whitespace-nowrap">
                  {event.source}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center text-sm text-gray-600 mb-2">
          <Clock className="w-4 h-4 mr-2" />
          {event.isOngoing ? (
            <span className="font-medium">
              Ongoing until {formatDate(event.endDate)} • {event.time}
            </span>
          ) : event.startDate && event.endDate ? (
            <span className="font-medium">
              {formatDateRange(event.startDate, event.endDate)} • {event.time}
            </span>
          ) : (
            <span className="font-medium">{formatDate(event.date)} at {event.time}</span>
          )}
        </div>
        
        <div className="flex items-center text-sm text-gray-600 mb-3">
          <MapPin className="w-4 h-4 mr-2" />
          <span>{event.location}</span>
        </div>
        
        <p className="text-gray-700 text-sm mb-3 line-clamp-2">{event.description}</p>
        
        <div className="flex justify-between items-center">
          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
            {event.category}
          </span>
          <a 
            href={event.isDuplicate ? event.allSources[0].url : event.url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center transition-colors"
          >
            View Details <ExternalLink className="w-3 h-3 ml-1" />
          </a>
        </div>
      </div>
    );
  };

  const CalendarView = () => {
    const eventsByDate = paginatedEvents.reduce((acc, event) => {
      if (!acc[event.date]) acc[event.date] = [];
      acc[event.date].push(event);
      return acc;
    }, {});

    const sortedDates = Object.keys(eventsByDate).sort();

    return (
      <div className="space-y-6">
        {sortedDates.map(date => (
          <div key={date} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 border-b pb-2">
              {formatDate(date)}
            </h3>
            <div className="space-y-3">
              {eventsByDate[date].map(event => (
                <div key={event.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-gray-900">{event.title}</div>
                      {getEventTypeIcon(event) && (
                        <span className="text-blue-600" title={getEventTypeLabel(event)}>
                          {getEventTypeIcon(event)}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">{event.time} • {event.location}</div>
                    {getEventTypeLabel(event) && (
                      <div className="text-xs text-blue-600 font-medium">{getEventTypeLabel(event)}</div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <div className="flex items-center gap-2">
                      <HeartIcon eventId={event.id} className="scale-90" />
                      {event.isDuplicate ? (
                        <button
                          onClick={() => setSourceSlideOut(event)}
                          className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded hover:bg-green-200 transition-colors cursor-pointer"
                          title="Click to view all sources"
                        >
                          📊 {event.duplicateCount} sources
                        </button>
                      ) : (
                        <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {event.source}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Seattle Events</h1>
                <p className="text-gray-600">Aggregated from across the city's best event sources</p>
              </div>
              
              <div className="flex items-center space-x-4">
                {lastUpdated && (
                  <div className="text-sm text-gray-500">
                    Last updated: {lastUpdated.toLocaleTimeString()}
                  </div>
                )}
                <button
                  onClick={scrapeEvents}
                  disabled={loading}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters and Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-8 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>

            {/* Source Filter */}
            <select
              value={selectedSource}
              onChange={(e) => setSelectedSource(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Sources</option>
              {sources.map(source => (
                <option key={source} value={source}>{source}</option>
              ))}
            </select>

            {/* Event Type Filter */}
            <select
              value={eventTypeFilter}
              onChange={(e) => setEventTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Event Types</option>
              <option value="single">Single Events</option>
              <option value="recurring">Recurring Events</option>
              <option value="multiDay">Multi-day Events</option>
              <option value="ongoing">Ongoing Events</option>
            </select>

            {/* Date Filter */}
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />

            {/* Favorites Filter */}
            <button
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              className={`flex items-center justify-center px-3 py-2 rounded-lg border transition-colors ${
                showFavoritesOnly
                  ? 'bg-red-50 border-red-300 text-red-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              title={showFavoritesOnly ? 'Show all events' : 'Show favorites only'}
            >
              <Heart className={`w-4 h-4 mr-1 ${showFavoritesOnly ? 'fill-red-500' : ''}`} />
              Favorites
              {favorites.size > 0 && (
                <span className="ml-1 text-xs bg-red-100 text-red-800 px-1.5 py-0.5 rounded-full">
                  {favorites.size}
                </span>
              )}
            </button>

            {/* View Toggle */}
            <div className="flex rounded-lg border border-gray-300 overflow-hidden lg:col-span-2">
              <button
                onClick={() => setViewMode('list')}
                className={`flex-1 flex items-center justify-center px-4 lg:px-6 py-2 text-sm font-medium min-w-[80px] lg:min-w-[100px] ${
                  viewMode === 'list' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <List className="w-4 h-4 mr-1" />
                List
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex-1 flex items-center justify-center px-4 lg:px-6 py-2 text-sm font-medium min-w-[80px] lg:min-w-[100px] ${
                  viewMode === 'calendar' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Calendar className="w-4 h-4 mr-1" />
                Calendar
              </button>
            </div>
          </div>

          {/* Results Summary */}
          <div className="space-y-3 text-sm text-gray-600">
            {/* Top row - Results and main controls */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex flex-col xs:flex-row xs:items-center gap-3">
                <span className="font-medium">
                  {filteredEvents.length > eventsPerPage 
                    ? `Showing ${startIndex + 1}-${Math.min(endIndex, filteredEvents.length)} of ${filteredEvents.length} events (Page ${currentPage} of ${totalPages})`
                    : `Showing ${filteredEvents.length} of ${events.length} events`}
                  {showFavoritesOnly && ` (favorites only)`}
                </span>
                <label className="flex items-center whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={showRecurringInstances}
                    onChange={(e) => setShowRecurringInstances(e.target.checked)}
                    className="mr-2"
                  />
                  Show recurring instances
                </label>
              </div>
              
              {/* Favorites clear button */}
              {favorites.size > 0 && (
                <button
                  onClick={clearAllFavorites}
                  className="text-red-600 hover:text-red-800 text-sm font-medium whitespace-nowrap self-start sm:self-auto"
                >
                  Clear all favorites ({favorites.size})
                </button>
              )}
            </div>

            {/* Bottom row - Sources (hidden on mobile, shown on tablet+) */}
            <div className="hidden md:flex items-center gap-2">
              <span className="text-gray-500 font-medium">Sources:</span>
              <div className="flex flex-wrap gap-2">
                {mockEventSources.map(source => (
                  <span key={source.name} className="text-xs bg-gray-100 px-2 py-1 rounded whitespace-nowrap">
                    {source.name}
                  </span>
                ))}
              </div>
            </div>

            {/* Mobile sources indicator */}
            <div className="md:hidden flex items-center gap-2">
              <span className="text-gray-500 font-medium">Sources:</span>
              <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                {mockEventSources.length} websites
              </span>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-blue-600 mb-4" />
            <p className="text-gray-600">Fetching latest events...</p>
          </div>
        )}

        {/* API Error Display */}
        {!loading && apiError && (
          <div className="mb-8 bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center mb-3">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-red-800">{apiError.message}</h3>
                <p className="text-red-700 mt-1">{apiError.suggestion}</p>
                {apiError.type === 'adblocker' && (
                  <div className="mt-3 text-sm text-red-600">
                    <p className="font-medium">How to fix this:</p>
                    <ul className="list-disc list-inside mt-1">
                      <li>Disable your ad blocker temporarily</li>
                      <li>Or add "seattle-events-aggregator.pages.dev" to your ad blocker's allowlist</li>
                      <li>Then refresh the page</li>
                    </ul>
                    <p className="mt-2 text-xs text-red-500">
                      Note: This site now uses a proxy to avoid ad blocker issues. If you're still seeing this error, please report it.
                    </p>
                  </div>
                )}
                {(apiError.type === 'proxy_failed' || apiError.type === 'proxy_config') && (
                  <div className="mt-3 text-sm text-red-600">
                    <p className="font-medium">Technical details:</p>
                    <ul className="list-disc list-inside mt-1">
                      <li>The API proxy service is not working correctly</li>
                      <li>This should resolve ad blocker issues automatically</li>
                      <li>Try refreshing the page in a few moments</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={scrapeEvents}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Events Display */}
        {!loading && !apiError && (
          <>
            {viewMode === 'list' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedEvents.map(event => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            ) : (
              <CalendarView />
            )}

            {filteredEvents.length === 0 && (
              <div className="text-center py-12">
                <Filter className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No events found matching your criteria.</p>
                <p className="text-gray-400 text-sm">Try adjusting your filters or search terms.</p>
              </div>
            )}

            {/* Pagination Controls */}
            {filteredEvents.length > eventsPerPage && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8 pt-6 border-t border-gray-200">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    Showing {startIndex + 1}-{Math.min(endIndex, filteredEvents.length)} of {filteredEvents.length} events
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="First page"
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Previous page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  
                  <span className="px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded">
                    Page {currentPage} of {totalPages}
                  </span>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Next page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Last page"
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Enhanced Slide-out Panel */}
      {sourceSlideOut && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setSourceSlideOut(null)}
          />
          
          {/* Slide-out Panel */}
          <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex justify-between items-center p-6 border-b">
                <div className="flex-1">
                  {sourceSlideOut.isDuplicate ? (
                    <>
                      <h3 className="text-lg font-semibold text-gray-900">Event Sources</h3>
                      <p className="text-sm text-gray-600">Found on {sourceSlideOut.duplicateCount} websites</p>
                    </>
                  ) : sourceSlideOut.eventType === 'recurring' || sourceSlideOut.isRecurringInstance ? (
                    <>
                      <h3 className="text-lg font-semibold text-gray-900">Recurring Event</h3>
                      <p className="text-sm text-gray-600">
                        {sourceSlideOut.recurrence?.frequency === 'weekly' && 'Weekly event'}
                        {sourceSlideOut.recurrence?.frequency === 'daily' && 'Daily event'}
                        {sourceSlideOut.recurrence?.frequency === 'monthly' && 'Monthly event'}
                      </p>
                    </>
                  ) : sourceSlideOut.eventType === 'multiDay' || sourceSlideOut.isMultiDayInstance ? (
                    <>
                      <h3 className="text-lg font-semibold text-gray-900">Multi-Day Event</h3>
                      <p className="text-sm text-gray-600">
                        {sourceSlideOut.startDate && sourceSlideOut.endDate ? 
                          `${formatDateRange(sourceSlideOut.startDate, sourceSlideOut.endDate)}` :
                          'Part of multi-day event'
                        }
                      </p>
                    </>
                  ) : sourceSlideOut.isOngoing ? (
                    <>
                      <h3 className="text-lg font-semibold text-gray-900">Ongoing Event</h3>
                      <p className="text-sm text-gray-600">Long-running exhibition</p>
                    </>
                  ) : (
                    <>
                      <h3 className="text-lg font-semibold text-gray-900">Event Details</h3>
                      <p className="text-sm text-gray-600">Single event</p>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <HeartIcon eventId={sourceSlideOut.id} />
                  <button
                    onClick={() => setSourceSlideOut(null)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 mb-2">{sourceSlideOut.title}</h4>
                  <div className="text-sm text-gray-600 mb-1">
                    📅 {sourceSlideOut.isOngoing ? 
                      `Ongoing until ${formatDate(sourceSlideOut.endDate)}` :
                      `${formatDate(sourceSlideOut.date || sourceSlideOut.startDate)} at ${sourceSlideOut.time}`
                    }
                  </div>
                  <div className="text-sm text-gray-600">
                    📍 {sourceSlideOut.location}
                  </div>
                </div>

                {/* Duplicate Sources Content */}
                {sourceSlideOut.isDuplicate && (
                  <div className="space-y-4">
                    <h5 className="font-medium text-gray-700 text-sm uppercase tracking-wide">
                      Source Versions ({sourceSlideOut.allSources.length})
                    </h5>
                    
                    {sourceSlideOut.allSources.map((source, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 border">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <div className="font-medium text-blue-600 mb-1">{source.source}</div>
                            <div className="text-xs text-gray-500">
                              {source.source === 'SeattleMet' && 'seattlemet.com'}
                              {source.source === 'EverOut' && 'everout.com'}
                              {source.source === 'VisitSeattle' && 'visitseattle.org'}
                              {source.source === 'SeattleMag' && 'seattlemag.com'}
                              {source.source === 'Fever' && 'feverup.com'}
                            </div>
                          </div>
                          <div className="text-xs bg-white px-2 py-1 rounded border">
                            {source.originalTime}
                          </div>
                        </div>
                        
                        <div className="text-sm text-gray-900 mb-2 font-medium">
                          {source.originalTitle}
                        </div>
                        
                        <a
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          View on {source.source} <ExternalLink className="w-3 h-3 ml-1" />
                        </a>
                      </div>
                    ))}

                    <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                      <div className="text-sm text-blue-800">
                        <strong>How we merge duplicates:</strong> We automatically detect when the same event 
                        appears on multiple sites and combine them to show you the most complete information. 
                        All original sources are preserved here for your reference.
                      </div>
                    </div>
                  </div>
                )}

                {/* Recurring Event Content */}
                {(sourceSlideOut.eventType === 'recurring' || sourceSlideOut.isRecurringInstance) && !sourceSlideOut.isDuplicate && (
                  <div className="space-y-4">
                    <h5 className="font-medium text-gray-700 text-sm uppercase tracking-wide">
                      Recurrence Pattern
                    </h5>
                    
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-sm text-blue-900 mb-2">
                        <strong>Frequency:</strong> {sourceSlideOut.recurrence?.frequency || 'Weekly'}
                      </div>
                      {sourceSlideOut.recurrence?.daysOfWeek && (
                        <div className="text-sm text-blue-900 mb-2">
                          <strong>Days:</strong> {sourceSlideOut.recurrence.daysOfWeek.join(', ')}
                        </div>
                      )}
                      {sourceSlideOut.recurrence?.endDate && (
                        <div className="text-sm text-blue-900">
                          <strong>Until:</strong> {formatDate(sourceSlideOut.recurrence.endDate)}
                        </div>
                      )}
                    </div>

                    <h5 className="font-medium text-gray-700 text-sm uppercase tracking-wide mt-6">
                      Upcoming Instances
                    </h5>

                    <div className="space-y-2">
                      {/* Generate a few upcoming dates for display */}
                      {(() => {
                        const upcomingDates = [];
                        const today = new Date();
                        const currentDate = new Date(sourceSlideOut.date || today);
                        
                        for (let i = 0; i < 5; i++) {
                          if (sourceSlideOut.recurrence?.frequency === 'weekly') {
                            upcomingDates.push(new Date(currentDate.getTime() + (i * 7 * 24 * 60 * 60 * 1000)));
                          } else if (sourceSlideOut.recurrence?.frequency === 'daily') {
                            // Show next few instances for specified days
                            const nextDate = new Date(currentDate);
                            nextDate.setDate(nextDate.getDate() + i);
                            const dayName = nextDate.toLocaleDateString('en-US', { weekday: 'long' });
                            if (sourceSlideOut.recurrence.daysOfWeek?.includes(dayName)) {
                              upcomingDates.push(nextDate);
                            }
                          }
                        }
                        
                        return upcomingDates.slice(0, 5).map((date, index) => (
                          <div key={index} className="bg-gray-50 rounded p-3 border">
                            <div className="text-sm font-medium text-gray-900">
                              {formatDate(date.toISOString().split('T')[0])} at {sourceSlideOut.time}
                            </div>
                            <div className="text-xs text-gray-600">
                              {date.toLocaleDateString('en-US', { weekday: 'long' })}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>

                    <div className="mt-6 p-4 bg-green-50 rounded-lg">
                      <div className="text-sm text-green-800">
                        <strong>Recurring events:</strong> This event happens regularly according to the schedule above. 
                        We show individual instances in your calendar to help with planning.
                      </div>
                    </div>
                  </div>
                )}

                {/* Multi-Day Event Content */}
                {(sourceSlideOut.eventType === 'multiDay' || sourceSlideOut.isMultiDayInstance) && !sourceSlideOut.isDuplicate && (
                  <div className="space-y-4">
                    <h5 className="font-medium text-gray-700 text-sm uppercase tracking-wide">
                      Daily Schedule
                    </h5>
                    
                    {sourceSlideOut.dailySchedule ? (
                      <div className="space-y-3">
                        {sourceSlideOut.dailySchedule.map((day, index) => (
                          <div key={index} className="bg-gray-50 rounded-lg p-4 border">
                            <div className="flex justify-between items-start mb-2">
                              <div className="font-medium text-gray-900">
                                {formatDate(day.date)}
                              </div>
                              <div className="text-xs bg-white px-2 py-1 rounded border">
                                {day.times.join(', ')}
                              </div>
                            </div>
                            <div className="text-sm text-blue-700 font-medium">
                              {day.focus}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-purple-50 rounded-lg p-4">
                        <div className="text-sm text-purple-900">
                          <strong>Duration:</strong> {sourceSlideOut.startDate && sourceSlideOut.endDate ? 
                            formatDateRange(sourceSlideOut.startDate, sourceSlideOut.endDate) : 
                            'Multi-day event'
                          }
                        </div>
                        <div className="text-sm text-purple-900 mt-1">
                          Check the event website for detailed daily schedules and timing.
                        </div>
                      </div>
                    )}

                    <div className="mt-6 p-4 bg-purple-50 rounded-lg">
                      <div className="text-sm text-purple-800">
                        <strong>Multi-day events:</strong> This event spans multiple days with different activities 
                        or sessions each day. Each day appears separately in your calendar for easy planning.
                      </div>
                    </div>
                  </div>
                )}

                {/* Ongoing Event Content */}
                {sourceSlideOut.isOngoing && !sourceSlideOut.isDuplicate && (
                  <div className="space-y-4">
                    <h5 className="font-medium text-gray-700 text-sm uppercase tracking-wide">
                      Exhibition Details
                    </h5>
                    
                    <div className="bg-amber-50 rounded-lg p-4">
                      <div className="text-sm text-amber-900 mb-2">
                        <strong>Duration:</strong> {formatDate(sourceSlideOut.startDate)} - {formatDate(sourceSlideOut.endDate)}
                      </div>
                      <div className="text-sm text-amber-900 mb-2">
                        <strong>Hours:</strong> {sourceSlideOut.time}
                      </div>
                      <div className="text-sm text-amber-900">
                        <strong>Type:</strong> Long-running exhibition
                      </div>
                    </div>

                    <div className="mt-6 p-4 bg-amber-50 rounded-lg">
                      <div className="text-sm text-amber-800">
                        <strong>Ongoing events:</strong> This exhibition or event runs continuously for an extended period. 
                        You can visit any time during the specified hours within the date range.
                      </div>
                    </div>
                  </div>
                )}

                {/* Single Event Content */}
                {!sourceSlideOut.isDuplicate && !sourceSlideOut.isRecurringInstance && !sourceSlideOut.isMultiDayInstance && !sourceSlideOut.isOngoing && sourceSlideOut.eventType === 'single' && (
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm text-gray-700">
                        This is a one-time event happening on {formatDate(sourceSlideOut.date)} at {sourceSlideOut.time}.
                      </div>
                    </div>
                    
                    <div className="text-sm text-gray-600">
                      <strong>Category:</strong> {sourceSlideOut.category}
                    </div>
                    <div className="text-sm text-gray-600">
                      <strong>Source:</strong> {sourceSlideOut.source}
                    </div>
                  </div>
                )}

                {/* Bottom Action */}
                <div className="mt-6 pt-4 border-t">
                  <a
                    href={sourceSlideOut.isDuplicate ? sourceSlideOut.allSources[0].url : sourceSlideOut.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-blue-600 text-white text-center py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                  >
                    View Full Event Details <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EventsAggregator;